import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { api } from 'src/api';
import { AppContext } from 'src/hooks/AppProvider';
import { EUserTier } from 'src/interfaces';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Form, Input } from 'antd';
import { Label } from 'src/components/ui/label';

type AddressNotesRefactorProps = {
  address: string | undefined;
};

export const AddressNotesRefactor = ({
  address,
}: AddressNotesRefactorProps) => {
  const { user } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchData = async () => {
      if (!address) {
        return;
      }
      try {
        const res = await api.blockchain.getAddressNotes(address);
        form.setFieldsValue({ notes: res?.notes ?? '' });
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch notes');
      }
    };

    if (isOpen) {
      void fetchData();
    }
  }, [address, isOpen, form]);

  const handleSave = async () => {
    if (!address) {
      return;
    }

    try {
      const values = await form.validateFields();
      setLoading(true);
      await api.blockchain.saveAddressNotes(address, values.notes);
      toast.success('Notes saved successfully');
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Error saving notes');
    } finally {
      setLoading(false);
    }
  };

  if (user?.accountType !== EUserTier.INTERNAL) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <FileText className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Address Notes</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={address} disabled />
          </div>
          <Form form={form} layout="vertical">
            <Form.Item
              name="notes"
              label="Notes"
              rules={[{ required: true, message: 'Please enter notes' }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Add notes here"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const textarea = e.currentTarget;
                    const cursorPosition = textarea.selectionStart;
                    const text = textarea.value;
                    const newText =
                      text.slice(0, cursorPosition) +
                      '\n' +
                      text.slice(cursorPosition);
                    form.setFieldsValue({ notes: newText });
                    setTimeout(() => {
                      textarea.setSelectionRange(
                        cursorPosition + 1,
                        cursorPosition + 1,
                      );
                    }, 0);
                  }
                }}
              />
            </Form.Item>
          </Form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
